import React from 'react';

const LONG_PRESS_THRESHOLD_MS = 500;
const TOUCH_MOVE_TOLERANCE_PX = 8;

const useMessageLongPress = ({ onActivate, onFocusActionable } = {}) => {
    const [selectedIds, setSelectedIds] = React.useState([]);
    const touchTimerRef = React.useRef(null);
    const touchStartPosRef = React.useRef(null);
    const triggeredLongPressRef = React.useRef(false);
    const onActivateRef = React.useRef(onActivate);
    const onFocusActionableRef = React.useRef(onFocusActionable);

    React.useEffect(() => { onActivateRef.current = onActivate; }, [onActivate]);
    React.useEffect(() => { onFocusActionableRef.current = onFocusActionable; }, [onFocusActionable]);

    const isSelecting = selectedIds.length > 0;

    const clearTouchTimer = () => {
        if (touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
            touchTimerRef.current = null;
        }
    };

    const fireActivate = (msg, anchorEl) => {
        if (typeof onActivateRef.current === 'function') {
            onActivateRef.current(msg, anchorEl);
        }
        if (typeof onFocusActionableRef.current === 'function') {
            onFocusActionableRef.current(msg, anchorEl);
        }
    };

    const toggleSelect = React.useCallback((messageId) => {
        if (messageId == null) return;
        setSelectedIds(prev => (
            prev.includes(messageId)
                ? prev.filter(id => id !== messageId)
                : [...prev, messageId]
        ));
    }, []);

    const clearSelect = React.useCallback(() => {
        setSelectedIds([]);
    }, []);

    const openActionSheet = React.useCallback((msg, anchorEl) => {
        fireActivate(msg, anchorEl);
    }, []);

    const bind = React.useCallback((msg) => ({
        onContextMenu: (event) => {
            event.preventDefault();
            fireActivate(msg, event.currentTarget);
        },
        onTouchStart: (event) => {
            if (event.touches.length !== 1) {
                clearTouchTimer();
                return;
            }
            triggeredLongPressRef.current = false;
            const touch = event.touches[0];
            touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
            const anchorEl = event.currentTarget;
            clearTouchTimer();
            touchTimerRef.current = setTimeout(() => {
                triggeredLongPressRef.current = true;
                touchTimerRef.current = null;
                fireActivate(msg, anchorEl);
            }, LONG_PRESS_THRESHOLD_MS);
        },
        onTouchMove: (event) => {
            const start = touchStartPosRef.current;
            if (!start || event.touches.length !== 1) return;
            const t = event.touches[0];
            const dx = Math.abs(t.clientX - start.x);
            const dy = Math.abs(t.clientY - start.y);
            if (dx > TOUCH_MOVE_TOLERANCE_PX || dy > TOUCH_MOVE_TOLERANCE_PX) {
                clearTouchTimer();
            }
        },
        onTouchEnd: () => {
            clearTouchTimer();
            touchStartPosRef.current = null;
        },
        onTouchCancel: () => {
            clearTouchTimer();
            touchStartPosRef.current = null;
            triggeredLongPressRef.current = false;
        },
        onClick: (event) => {
            if (event.shiftKey) {
                event.preventDefault();
                fireActivate(msg, event.currentTarget);
            }
        },
    }), []);

    React.useEffect(() => () => clearTouchTimer(), []);

    const cancelTimer = React.useCallback(() => {
        clearTouchTimer();
        touchStartPosRef.current = null;
    }, []);

    return {
        bind,
        isSelecting,
        selectedIds,
        toggleSelect,
        clearSelect,
        openActionSheet,
        cancelTimer,
    };
};

export default useMessageLongPress;
